import cn from 'classnames'
import Link, { LinkProps } from 'next/link'
import React, { ReactNode } from 'react'
import Masonry from 'react-masonry-component'
import { ImageFragment } from '../src/model/fragments'
import { ConditionalWrap } from './ConditionalWrap'
import { Lightbox } from './Lightbox'
import { Picture, PictureFit } from './Picture'
import { ReferencesProps } from './References'

export interface MasonryProps {
	items: (Omit<ReferencesProps['items'][number], 'products' | 'linkProps'> & {
		onClick?: () => void
		linkProps?: LinkProps
		title?: ReactNode
		label?: ReactNode
	})[],
	closeCallback?: () => void
	changeCallback?: (index: number) => void
}

const GalleryMasonryItem: React.FunctionComponent<{
	name: string
	onClick: () => void
	image: ImageFragment | undefined
	linkProps?: LinkProps
}> = ({ name, onClick, image, linkProps }) => {
	

	return <div className="galleryMasonry-item">
		<ConditionalWrap
			condition={Boolean(linkProps)}
			wrap={(children) => (
				<Link {...linkProps!}>
					<a className="galleryMasonry-item-in" title={name} onClick={onClick}>
						{children}
					</a>
				</Link>
			)}
		>
			<ConditionalWrap
				condition={!Boolean(linkProps)}
				wrap={(children) => (
					<button
						className="galleryMasonry-item-in"
						type="button"
						title={name}
						onClick={onClick}
					>
						{children}
					</button>
				)}
			>
				<>
					{image && (
						<Picture
							image={image}
							width={400}
							height={400 * ((image.intrinsicHeight && image.intrinsicWidth) ? (image.intrinsicHeight / image.intrinsicWidth) : 1)}
							alt={name}
							layout={PictureFit.Widen}
						/>
					)}
				</>
			</ConditionalWrap>
		</ConditionalWrap>
	</div>
}

export const GalleryMasonry: React.FunctionComponent<MasonryProps> = ({
	items,
	closeCallback,
	changeCallback
}) => {
	const [lightboxIndex, setLightboxIndex] = React.useState(0)
	const [isOpen, setIsOpen] = React.useState(false)

	React.useCallback(() => {
		if(changeCallback)
		{
			changeCallback(lightboxIndex);
		}
	}, [lightboxIndex]);
	
	return (
		<Masonry elementType="div" className={cn('galleryMasonry')}>
			{items.map((item, i) => (
				<GalleryMasonryItem
					key={i}
					name={item.name}
					onClick={() => {
						if (item.onClick) {
							item.onClick()
						} else {
							setLightboxIndex(i)
							setIsOpen(true)
						}
					}}
					image={(item.listingImage ?? item.image)! as ImageFragment}
					linkProps={item.linkProps}
				/>
			))}
			{isOpen && (
				<Lightbox
					items={items.map((item) => ({
						url: (item.image ?? item.listingImage)?.previewUrl || '',
						title: item.title,
						label: item.label
					}))}
					index={lightboxIndex}
					setIndex={setLightboxIndex}
					onCloseRequest={() => { 
						setIsOpen(false);
						if(closeCallback)
						{
							closeCallback();
						}
					}}
					onChangeRequest={(i) => {
						if(changeCallback)
						{
							changeCallback(i);
						}
					}}
				/>
			)}
		</Masonry>
	)
}
